import { useCallback, useEffect, useState } from 'react';
import { tiktokService, TiktokAdAccount } from '@/services/tiktok.service';

interface State {
  loading: boolean;
  account: TiktokAdAccount | null;
  currency: string | null;
  refresh: () => Promise<void>;
}

let cached: TiktokAdAccount | null | undefined;
const listeners = new Set<(acc: TiktokAdAccount | null) => void>();

async function load() {
  try {
    cached = await tiktokService.getDefault();
  } catch {
    cached = null;
  }
  listeners.forEach((l) => l(cached ?? null));
}

export function refreshTiktokAccountCurrency() {
  return load();
}

export function useTiktokAccountCurrency(): State {
  const [account, setAccount] = useState<TiktokAdAccount | null>(cached ?? null);
  const [loading, setLoading] = useState<boolean>(cached === undefined);

  useEffect(() => {
    const onChange = (acc: TiktokAdAccount | null) => setAccount(acc);
    listeners.add(onChange);
    if (cached === undefined) {
      setLoading(true);
      load().finally(() => setLoading(false));
    }
    return () => { listeners.delete(onChange); };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await load();
    setLoading(false);
  }, []);

  return { loading, account, currency: account?.currency ?? null, refresh };
}
